import { useCallback, useMemo } from "react";
import { FileUploadType } from "@fixa/platform-shared/dist/fileUpload/fileUpload.type";
import { useWriteSignedUrls } from "@coworker/apprestructured/src/task-management/services/useWriteSignedUrls";
import { useUploadFilesToBucket } from "@coworker/apprestructured/src/task-management/services/useUploadFilesToBucket";
import { filesToCreateUploads } from "@coworker/apprestructured/src/task-management/util";
import {
  calcHasSignedUrlsForAllFiles,
  convertToSignedFiles,
  NO_CREATE_UPLOADS,
  UploaderResult,
} from "@coworker/apprestructured/src/uploader/uploader.helper";

/**
 * Helper for uploading files to a bucket.
 *
 * @param files
 */
export function useFileUploader(files: File[]): UploaderResult {
  const fileNames = useMemo(() => {
    return files.map((file) => file.name);
  }, [files]);

  const { data: signedUrlResponses } = useWriteSignedUrls(
    fileNames,
    FileUploadType.FILE
  );

  const hasSignedUrlsForAllFiles = useMemo(() => {
    return calcHasSignedUrlsForAllFiles(files, signedUrlResponses);
  }, [files, signedUrlResponses]);

  const { mutateAsync: uploadFilesToBucketMutation } = useUploadFilesToBucket();

  const ready = hasSignedUrlsForAllFiles;

  const upload = useCallback(async () => {
    if (!ready) {
      throw new Error("File uploader is not ready");
    }
    const signedFiles = convertToSignedFiles(files, signedUrlResponses);
    return uploadFilesToBucketMutation(signedFiles);
  }, [files, ready, uploadFilesToBucketMutation, signedUrlResponses]);

  const createUploads = useMemo(() => {
    if (!hasSignedUrlsForAllFiles) {
      return NO_CREATE_UPLOADS;
    }
    return filesToCreateUploads(files, signedUrlResponses);
  }, [files, hasSignedUrlsForAllFiles, signedUrlResponses]);

  return { ready, upload, createUploads };
}
